import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { BiLandscape } from 'react-icons/bi';
import EntityTiles from './EntityTiles';
import PipelineBoard from './PipelineBoard';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import ActivityChart30d from 'Utils/ActivityChart30d';
import { useActivityWindowDays, ActivityWindowPicker, windowLabel } from 'Utils/ActivityWindow';
import FunnelBar from 'Utils/FunnelBar';
import { BRAND, INK } from 'Utils/styleTokens';
import AppContext from 'Utils/AppContext';
import { buildCensus, censusCounts, censusLabel, excludedNote } from 'Utils/sourceCensus';
import { RISK_TIERS, riskTierCounts } from 'Utils/riskTier';
import { getLandscapeHomeSummary } from 'SensitiveDataLandscape/homeSummaryApi';
import { listDiscoverJobsPage } from 'DataDiscovery/discoverApi';
import { listProfileJobs } from 'Metadata/profileApi';
import { listMaskingJobs, deriveMaskStatusByDataSource, readableJobError } from 'Secure/api';
import { fmtDateTime } from 'Utils/formatDate';

// Mockup 2a activity legend: meta scans cyan, discoveries blue, mask jobs violet. Failures are
// NOT a series of their own — a failed job stacks in its own family's bar and is called out in
// the day's drilldown, in neutral ink.
const ACTIVITY_SERIES = [
  { key: 'profile', label: 'Fingerprint scans', color: BRAND.cyan },
  { key: 'discover', label: 'Discoveries', color: BRAND.blue },
  { key: 'mask', label: 'Mask jobs', color: BRAND.violet },
];

const FAILED_STATUSES = new Set(['FAILED', 'ERROR', 'TIMEOUT', 'INTERRUPTED']);

// Kind label + a route to that job's results (or error) for the activity drilldown.
const KIND_META = {
  profile: { kind: 'Fingerprint scan', to: (j) => (j.dataSourceId != null ? `/metadata/source/${encodeURIComponent(j.dataSourceId)}` : null) },
  discover: { kind: 'Discovery', to: (j) => (j.id != null ? `/results/${encodeURIComponent(j.id)}` : null) },
  mask: { kind: 'Mask', to: (j) => (j.dataSourceId != null ? `/secure/mask/inventory/${encodeURIComponent(j.dataSourceId)}` : null) },
};

const jobEvent = (job, key) => {
  const failed = FAILED_STATUSES.has(String(job.status || '').toUpperCase());
  const meta = KIND_META[key] || {};
  return {
    ts: job.startedAt || job.finishedAt,
    // A failed job keeps its family's key so it is still counted as activity; `error` below is
    // what the drilldown renders it by.
    key,
    id: job.id,
    kind: meta.kind || key,
    name: job.dataSourceName || job.name || job.runLabel || (job.dataSourceId != null ? `Source ${job.dataSourceId}` : ''),
    status: job.status,
    to: meta.to ? meta.to(job) : null,
    // Human-readable summary (code + parsed message), never the raw upstream JSON blob.
    error: failed ? (readableJobError(job) || job.error || 'Job failed') : null,
  };
};

const Home = (props) => {
  const { connections } = useContext(AppContext) || {};
  const [homeSummary, setHomeSummary] = useState(null);
  // Shared trailing window (see Utils/ActivityWindow) — the same one the Data Discovery panels use.
  const [windowDays, setWindowDays] = useActivityWindowDays();
  // Whole-source mask status keyed by data-source id, distilled from the entity
  // roll-up payload — drives the Masked pipeline column and the protection funnel.
  const [maskStatusById, setMaskStatusById] = useState({});
  // Whole-source mask status derived from the masking-jobs list. The entity roll-up
  // (above) omits FILE / folder-search sources, so masked file sources are invisible
  // there; this map covers every source with a masking job and is unioned in below.
  const [jobMaskStatusById, setJobMaskStatusById] = useState({});
  const [activityEvents, setActivityEvents] = useState([]);
  const [profiledIds, setProfiledIds] = useState(new Set());
  // Data-source ids with an in-flight job (scan / discover / mask). Drives the pipeline
  // action buttons, which read "Running…" and disable while a job is active.
  const [runningById, setRunningById] = useState({});

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const response = await getLandscapeHomeSummary();
        setHomeSummary(response);
      } catch (e) {
        console.log('Error loading landscape home summary', e);
      }
    };
    const loadActivity = async () => {
      const results = await Promise.allSettled([
        listDiscoverJobsPage(0, 200),
        listProfileJobs({ page: 0, size: 200 }),
        listMaskingJobs({ page: 0, size: 200 }),
      ]);
      const [discoverRes, profileRes, maskRes] = results.map((r) =>
        r.status === 'fulfilled' ? r.value : null
      );
      const events = [];
      (discoverRes?.discoverJobs || []).forEach((j) => events.push(jobEvent(j, 'discover')));
      (profileRes?.content || []).forEach((j) => events.push(jobEvent(j, 'profile')));
      (maskRes?.content || []).forEach((j) => events.push(jobEvent(j, 'mask')));
      // Encrypt/decrypt are not on this chart at all: the 2a legend has no encrypt series, and
      // their failures used to ride in on the retired `error` series. Both live on /secure.
      setActivityEvents(events);

      // Any source with a live job of any family → its pipeline button shows "Running…".
      const ACTIVE_STATUSES = new Set([
        'RUNNING', 'QUEUED', 'PENDING', 'IN_PROGRESS', 'PROCESSING', 'STARTED', 'SCANNING', 'MASKING',
      ]);
      const running = {};
      const markRunning = (j) => {
        const status = String(j?.status || j?.state || '').toUpperCase();
        const id = Number(j?.dataSourceId);
        if (Number.isFinite(id) && ACTIVE_STATUSES.has(status)) running[id] = true;
      };
      (discoverRes?.discoverJobs || []).forEach(markRunning);
      (profileRes?.content || []).forEach(markRunning);
      (maskRes?.content || []).forEach(markRunning);
      setRunningById(running);

      // Whole-source mask status straight from the masking jobs — covers FILE / folder-search
      // sources the entity roll-up omits, so masked file sources reach the Masked column.
      setJobMaskStatusById(deriveMaskStatusByDataSource(maskRes?.content || []));

      setProfiledIds(
        new Set(
          (profileRes?.content || [])
            .map((j) => Number(j.dataSourceId))
            .filter((n) => Number.isFinite(n))
        )
      );
    };
    const loadAll = () => {
      loadSummary();
      loadActivity();
    };
    loadAll();
    // Poll so finished jobs fall out of "Running", the funnel/pipeline re-bucket, and the
    // activity chart picks up new runs without a manual reload.
    const timer = setInterval(loadAll, 30000);
    return () => clearInterval(timer);
  }, []);

  const onEntitiesLoaded = (tiles) => {
    const statusMap = {};
    (tiles || []).forEach((tile) => {
      (tile.sources || []).forEach((source) => {
        const prev = statusMap[source.dataSourceId];
        // Any ERROR wins for visibility; otherwise MASKED/MASKING beat UNMASKED.
        if (prev === 'ERROR') return;
        if (source.maskStatus === 'ERROR' || prev == null || prev === 'UNMASKED') {
          statusMap[source.dataSourceId] = source.maskStatus;
        }
      });
    });
    setMaskStatusById(statusMap);
  };

  // ONE census (Utils/sourceCensus) for the header, the funnel and the board: no landscape=none
  // rows (TDM staging sources, raw views, anything a user hid), no platform-managed rows, no
  // fan-out sub-sources. The Data Discovery and Secure landscapes count the same way, so the
  // three headers agree.
  const census = useMemo(() => buildCensus(homeSummary?.sources || [], connections), [homeSummary, connections]);
  const sources = census.visible;
  const testResultById = useMemo(() => {
    const map = {};
    (connections || []).forEach((c) => {
      if (c && c.id != null) map[String(c.id)] = c.lastTestSuccess === true;
    });
    return map;
  }, [connections]);
  // Union of the entity-roll-up statuses and the masking-job statuses. Both derive from the
  // same backend deriver so they agree where they overlap; the job map adds the FILE / folder-
  // search sources the roll-up leaves out. Roll-up wins on conflict (it's the whole-source view).
  const mergedMaskStatusById = useMemo(
    () => ({ ...jobMaskStatusById, ...maskStatusById }),
    [jobMaskStatusById, maskStatusById]
  );
  // Risk tiers for the header strip: one vocabulary (Utils/riskTier), every tier shown even at 0
  // — a tier that vanishes when empty reads as a different scale from screen to screen.
  const riskCounts = useMemo(() => riskTierCounts(sources), [sources]);
  // Funnel counts from the SAME bucketing rule the pipeline board uses (sourceStage), so the
  // header's four numbers sum to the board's four columns.
  const counts = useMemo(
    () => censusCounts(sources, { maskStatusById: mergedMaskStatusById, profiledIds, testResultById }),
    [sources, mergedMaskStatusById, profiledIds, testResultById]
  );
  const maskedPct = counts.total > 0 ? Math.round((counts.secured / counts.total) * 100) : 0;
  const notCounted = excludedNote(census.excluded);

  const headerMeta = useMemo(
    () => (
      <span className='sg-small' style={{ color: 'var(--ink-500)' }} data-testid='workflow-header-meta'>
        <span title={notCounted || undefined}>{censusLabel(counts)}</span>
        <span className='d-inline-flex flex-wrap align-items-center ml-2' style={{ gap: 6 }} data-testid='workflow-risk-tiers'>
          {RISK_TIERS.map((tier) => (
            <span
              key={tier.key}
              className='sg-pill sg-pill--nodot'
              style={{ background: tier.bg, color: tier.text }}
              title={tier.key === 'unknown' ? 'Sources with no completed discovery, or none that produced a risk score' : `${tier.label} risk`}
            >
              {riskCounts[tier.key]} {tier.label}
            </span>
          ))}
        </span>
      </span>
    ),
    [counts, riskCounts, notCounted]
  );

  return (
    <FetchContainer
      fetchingData={props.fetchingData}
      absolute={true}
      fluid
    >
      <div>
        <PageHeader
          icon={BiLandscape}
          header={'Data Privacy Workflow'}
          help='Every data source and how far it has moved: surfaced, fingerprinted, discovered, secured. Counts leave out hidden sources, platform-managed rows and fan-out sub-sources.'
          meta={headerMeta}
          tooltip={`Current snapshot of risk across your data sources. Last updated ${fmtDateTime(homeSummary?.generatedAt || Date.now())}.`}
          primaryAction={
            <FunnelBar
              caption='Protection funnel:'
              trailing={`${maskedPct}% secured`}
              segments={[
                { label: 'Surfaced', value: counts.surfaced, color: INK[300] },
                { label: 'Fingerprinted', value: counts.profiled, color: BRAND.cyan },
                { label: 'Discovered', value: counts.discovered, color: BRAND.blue },
                { label: 'Secured', value: counts.secured, color: BRAND.violet },
              ]}
            />
          }
        />
        <Row>
          <Col className='px-3'>
            <div className='d-flex align-items-center mb-2'>
              <div className='ml-auto'>
                <ActivityWindowPicker days={windowDays} onChange={setWindowDays} />
              </div>
            </div>
            <ActivityChart30d
              title={`Activity, ${windowLabel(windowDays)}`}
              series={ACTIVITY_SERIES}
              events={activityEvents}
              days={windowDays}
              drilldown
            />
          </Col>
        </Row>
        <Row>
          <Col className='px-3'>
            <EntityTiles onLoaded={onEntitiesLoaded} />
          </Col>
        </Row>
        <Row>
          <Col className='px-3'>
            <PipelineBoard
              sources={sources}
              maskStatusById={mergedMaskStatusById}
              runningById={runningById}
            />
          </Col>
        </Row>
      </div>
    </FetchContainer>
  );
}

export default Home;
